import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { collection, query, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function SinglePennantLadder() {
  const { pennantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [pennantName, setPennantName] = useState('');
  const [ladderData, setLadderData] = useState([]); 
  // ladderData will be an array of objects in the form:
  // [{ sectionKey: "1", teams: [ { name, totalPoints, weeklyPoints, ... }, ... ] }, ... ]
  const [sortedDates, setSortedDates] = useState([]); 
  // sortedDates is an array of date strings in dd/mm/yyyy format.
  const [expandedSections, setExpandedSections] = useState({}); // track which sections are expanded

  const toggleSectionExpand = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  useEffect(() => {
    if (!pennantId) return;

    (async () => {
      try {
        setLoading(true);

        // 1) Fetch the Pennant document to get its name.
        const pennantDoc = await getDoc(doc(db, 'Pennants', pennantId));
        if (pennantDoc.exists()) {
          setPennantName(pennantDoc.data().name || `Pennant: ${pennantId}`);
        } else {
          setPennantName(`No Ladder available for this pennant, check back later.`);
        }

        // 2) Fetch all "draws" sub-collection to map weekIds to dates.
        const drawsSnap = await getDocs(collection(db, 'Pennants', pennantId, 'draws'));
        const weekIdToDate = {}; // e.g. { "week-1": "25/01/2025" }
        const dateSet = new Set();

        drawsSnap.forEach((drawDoc) => {
          const drData = drawDoc.data();
          if (!Array.isArray(drData.weeks)) return;
          drData.weeks.forEach((wk) => {
            if (wk.id && wk.date) {
              weekIdToDate[wk.id] = wk.date;
              dateSet.add(wk.date);
            }
          });
        });

        // Sort the unique set of dates ascending.
        const sortedDateArr = [...dateSet].sort((a, b) => {
          const [dA, mA, yA] = a.split('/').map(Number);
          const [dB, mB, yB] = b.split('/').map(Number);
          return new Date(yA, mA - 1, dA) - new Date(yB, mB - 1, dB);
        });
        setSortedDates(sortedDateArr);

        // 3) Fetch all Matches that belong to this Pennant.
        const matchesSnap = await getDocs(query(collection(db, 'Matches')));
        // Build a map where each key is a team name and the value is an object with team data.
        const ladderMap = {};

        matchesSnap.forEach((mDoc) => {
          const matchData = mDoc.data();
          if (matchData.pennantId !== pennantId) return; // filter out other pennants

          const { team1, team2, section, weekId } = matchData;
          const dateStr = weekIdToDate[weekId] || null;

          [team1, team2].forEach((tm) => {
            if (!tm || tm.name === 'Bye') return;
            const teamName = tm.teamName || tm.name;
            if (!teamName) return;

            if (!ladderMap[teamName]) {
              ladderMap[teamName] = {
                name: teamName,
                section: section || tm.section || '1',
                totalPoints: 0,
                weeklyPoints: {},
              };
            }
            // Accumulate total points.
            ladderMap[teamName].totalPoints += (tm.total || 0);
            // Accumulate weekly points if a date is available.
            if (dateStr) {
              ladderMap[teamName].weeklyPoints[dateStr] =
                (ladderMap[teamName].weeklyPoints[dateStr] || 0) + (tm.total || 0);
            }
          });
        });

        // 4) Group teams by section.
        const groupMap = {};
        Object.values(ladderMap).forEach((tmObj) => {
          const sec = tmObj.section || '1';
          if (!groupMap[sec]) groupMap[sec] = [];
          groupMap[sec].push(tmObj);
        });

        // Sort each section's teams by totalPoints descending.
        Object.keys(groupMap).forEach((sec) => {
          groupMap[sec].sort((a, b) => b.totalPoints - a.totalPoints);
        });

        // Convert the grouped data to an array of objects.
        const finalLadderData = Object.keys(groupMap)
          .sort((a, b) => Number(a) - Number(b))
          .map((sectionKey) => ({
            sectionKey,
            teams: groupMap[sectionKey],
          }));

        setLadderData(finalLadderData);

        // Initialize all sections as expanded by default.
        const initialExpanded = {};
        finalLadderData.forEach((group) => {
          initialExpanded[group.sectionKey] = true;
        });
        setExpandedSections(initialExpanded);
      } catch (err) {
        console.error('Error loading SinglePennantLadder:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [pennantId]);

  if (!pennantId) {
    return <Typography variant="body1" color="error">No Pennant ID provided.</Typography>;
  }

  if (loading) {
    return <Typography>Loading Ladder Data...</Typography>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        {pennantName}
      </Typography>
      <Typography variant="h4" gutterBottom>
        Ladder
      </Typography>
      {ladderData.map((group) => {
        const { sectionKey, teams } = group;
        return (
          <Box key={sectionKey} sx={{ mb: 4, pl: 2 }}>
            {/* Section Header with expand/collapse toggle */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                bgcolor: '#d0d0ff',
                p: 1,
                borderRadius: 1,
                mb: 1,
              }}
              onClick={() => toggleSectionExpand(sectionKey)}
            >
              <Typography variant="subtitle1">
                Section {sectionKey}
              </Typography>
              <IconButton>
                {expandedSections[sectionKey] ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            {expandedSections[sectionKey] && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ p: '2px', minWidth: 0 }}>Team</TableCell>
                      {sortedDates.map((dateStr, idx) => (
                        <TableCell
                          key={dateStr}
                          align="center"
                          sx={{
                            bgcolor: idx % 2 === 0 ? '#f0f8ff' : '#f5f5f5',
                            p: '2px',
                            minWidth: 0,
                          }}
                        >
                          {dateStr}
                        </TableCell>
                      ))}
                      <TableCell
                        align="center"
                        sx={{
                          bgcolor: '#e8e8e8',
                          p: '2px',
                          minWidth: 0,
                        }}
                      >
                        Total Points
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teams.map((tm) => (
                      <TableRow key={tm.name}>
                        <TableCell sx={{ p: '2px', minWidth: 0 }}>{tm.name}</TableCell>
                        {sortedDates.map((dateStr) => (
                          <TableCell
                            key={`${tm.name}-${dateStr}`}
                            align="center"
                            sx={{ bgcolor: 'inherit', p: '2px', minWidth: 0 }}
                          >
                            {tm.weeklyPoints[dateStr] || 0}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ p: '2px', minWidth: 0 }}>
                          {tm.totalPoints}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
